import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaBars, FaSearch, FaTimes } from 'react-icons/fa';
import { ProductsContext, CartContext } from '../App';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';


const Header = () => {
  const { setSearchQuery, products } = useContext(ProductsContext);
  const { cart } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  

  const [scrollPosition, setScrollPosition] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false); // NEW: Prevent initial flash

  const closeBtnRef = useRef(null);
  const searchInputRef = useRef(null);

  const isHomepage = location.pathname === '/';
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const maxScrollForTransition = 300;

  // Initialize on mount
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('recentSearches');
      if (saved) {
        setRecentSearches(JSON.parse(saved));
      }
    } catch (e) {
      console.log('Could not load recent searches');
    }
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // scroll handling
  useEffect(() => {
    let ticking = false;
    const updateScroll = () => {
      setScrollPosition(window.scrollY);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScroll);
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // toggle body class + lock scroll when menu open
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const scrollY = window.scrollY;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.classList.add('menu-open');

    return () => {
      document.body.classList.remove('menu-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';

      window.scrollTo(0, scrollY);
    };
  }, [mobileMenuOpen]);

  // focus close button when menu opens & ESC to close
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') {
        if (mobileMenuOpen) setMobileMenuOpen(false);
        if (searchOpen) setSearchOpen(false);
      }
    }

    if (mobileMenuOpen || searchOpen) {
      setTimeout(() => closeBtnRef.current?.focus(), 0);
      window.addEventListener('keydown', handleKey);
    }

    return () => window.removeEventListener('keydown', handleKey);
  }, [mobileMenuOpen, searchOpen]);

  const progress = isHomepage
    ? Math.min(scrollPosition / maxScrollForTransition, 1)
    : 1;

  const headerStyle = {
    backgroundColor: `rgba(255, 255, 255, ${progress})`,
    boxShadow: `0 2px 6px rgba(0, 0, 0, ${0.06 * progress})`,
    // color: `rgb(${255 - 238 * progress}, ${255 - 238 * progress}, ${255 - 238 * progress})`,
  };

  const logoStyle = {
    color:`black`,
    fontSize: `${1.6 + (6 - 1.6) * (1 - progress)}rem`,
    letterSpacing: `${2 + (4 - 2) * (1 - progress)}px`,
    textShadow: `0 2px 6px rgba(0, 0, 0, ${0.35 * (1 - progress)}), 0 0 20px rgba(0, 0, 0, ${0.15 * (1 - progress)})`,
  };

  const handleSearch = (query) => {
    if (query.trim()) {
      setSearchQuery(query);
      
      // Add to recent searches
      const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
      setRecentSearches(updated);
      try {
        localStorage.setItem('recentSearches', JSON.stringify(updated));
      } catch (e) {
        console.log('Could not save recent searches');
      }
      
      setSearchOpen(false);
      setSearchInput('');
      setSuggestions([]);
      
      // Navigate to shop if not already there
      if (location.pathname !== '/shop') {
        navigate('/shop');
      }
    }
  };

  const handleInputChange = (value) => {
    setSearchInput(value);
    
    if (value.trim().length > 0) {
      const searchTerm = value.toLowerCase();
      
      // Filter products based on name, category, description, material, or color
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm) ||
        product.category.toLowerCase().includes(searchTerm) ||
        product.description.toLowerCase().includes(searchTerm) ||
        product.material.toLowerCase().includes(searchTerm) ||
        product.colors.some(color => color.toLowerCase().includes(searchTerm))
      );
      setSuggestions(filtered.slice(0, 6)); // Show top 6 suggestions
    } else {
      setSuggestions([]);
    }
  };

  const handleRecentClick = (search) => {
    setSearchInput(search);
    handleSearch(search);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem('recentSearches');
    } catch (e) {
      console.log('Could not clear recent searches');
    }
  };

  // Mobile menu content - ONLY RENDER WHEN OPEN
  const mobileMenuNode = mobileMenuOpen ? (
    <nav
      className="mobile-menu open"
      aria-label="Mobile navigation"
      role="dialog"
      aria-modal="true"
    >
      <button
        ref={closeBtnRef}
        className="mobile-menu-close"
        aria-label="Close menu"
        onClick={() => setMobileMenuOpen(false)}
      >
        <FaTimes />
      </button>

      <div className="mobile-menu-links">
        <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
        <Link to="/shop" onClick={() => setMobileMenuOpen(false)}>Shop</Link>
        <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
        <Link to="/cart" onClick={() => setMobileMenuOpen(false)}>Cart</Link>
      </div>
    </nav>
  ) : null;

  // Search modal content
  const searchModalNode = (
    <div 
      className={`search-modal ${searchOpen ? 'open' : ''}`}
      onClick={() => searchOpen && setSearchOpen(false)}
    >
      <div 
        className="search-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search jewelry, rings, necklaces..."
            value={searchInput}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch(searchInput);
              }
            }}
            className="search-modal-input"
          />
          <button
            className="search-close-btn"
            onClick={() => setSearchOpen(false)}
            aria-label="Close search"
          >
            <FaTimes />
          </button>
        </div> */}
       <div className="search-input-wrapper">
          <FaSearch className="search-icon" />

          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search jewelry, rings, necklaces..."
            value={searchInput}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch(searchInput);
              }
            }}
            className="search-modal-input"
          />

          {/* ✅ Single clear button */}
          {searchInput && (
            <button
              type="button"
              className="search-clear-btn"
              aria-label="Clear search"
              onClick={() => {
                setSearchInput('');
                setSuggestions([]);
                setSearchQuery('');
                searchInputRef.current?.focus();
              }}
            >
              <FaTimes />
            </button>
          )}
        </div>
        {/* Product Suggestions */}
        {suggestions.length > 0 && (
          <div className="product-suggestions">
            <h3 className="suggestions-title">Suggested Products</h3>
            <div className="suggestions-list">
              {suggestions.map((product) => (
                <button
                  key={product.id}
                  className="suggestion-item"
                  onClick={() => {
                    setSearchQuery(product.name);
                    setSearchOpen(false);
                    setSearchInput('');
                    setSuggestions([]);
                    if (location.pathname !== '/shop') {
                      navigate('/shop');
                    }
                  }}
                >
                  <div className="suggestion-image">
                    <img
                      src={
                        Array.isArray(product.images)
                          ? product.images[0]
                          : (product.images && typeof product.images === 'object')
                            ? Object.values(product.images)[0]
                            : 'https://via.placeholder.com/100x100?text=No+Image'
                      }
                      alt={product.name}
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/100x100?text=No+Image'; }}
                    />
                  </div>
                  <div className="suggestion-info">
                    <p className="suggestion-name">{product.name}</p>
                    <p className="suggestion-category">{product.category}</p>
                    <p className="suggestion-price">${(product.price || Object.values(product.prices || {})[0] || 0).toFixed(2)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {recentSearches.length > 0 && suggestions.length === 0 && (
          <div className="recent-searches">
            <div className="recent-searches-header">
              <h3>Recent Searches</h3>
              <button 
                className="clear-btn"
                onClick={clearRecentSearches}
              >
                Clear All
              </button>
            </div>
            <div className="recent-searches-list">
              {recentSearches.map((search, idx) => (
                <button
                  key={idx}
                  className="recent-search-item"
                  onClick={() => handleRecentClick(search)}
                >
                  <FaSearch className="recent-icon" />
                  <span>{search}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <button 
          className="search-submit"
          onClick={() => handleSearch(searchInput)}
          disabled={!searchInput.trim()}
        >
          Search
        </button>
      </div>
    </div>
  );

  return (
    <>
      <header
        className={`lux-header ${mobileMenuOpen ? 'menu-open' : ''}`}
        role="banner"
        style={headerStyle}
      >
        <div className="lux-left" />
        {/* <img width={'150px'} src='/images/SabrinaSilver.gif' alt='Sabrina Silver Logo' /> */}

        <Link to="/" className="header-logo" aria-label="Home" style={logoStyle}>
          Sabrina Silver
        </Link>

        <div className="lux-right">
          <Link to="/cart" className="cart-icon" aria-label="Cart">
            <FaShoppingCart />
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </Link>

          <button
            className="icon-button search-button"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
          >
            <FaSearch />
          </button>

          <button
            className="icon-button"
            aria-label="Open menu"
            onClick={() => setMobileMenuOpen((s) => !s)}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {typeof document !== 'undefined' && isInitialized && mobileMenuOpen && createPortal(mobileMenuNode, document.body)}
      {typeof document !== 'undefined' && searchOpen && createPortal(searchModalNode, document.body)}

    </>
  );
};

export default Header;